import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { CONTACT } from "../../content";
import { scrollToId } from "../../lib/scroll";

export const Nav = ({ t, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["services", t.nav.services],
    ["approach", t.nav.approach],
    ["about", t.nav.about],
    ["experience", t.nav.experience],
    ["contact", t.nav.contact],
  ];

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-bone/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="font-display text-2xl md:text-[1.7rem] leading-none text-ink tracking-tight"
        >
          Timo Schewik<span className="text-accent">.</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => go(id)}
              className="link-underline text-sm text-ink-2 hover:text-ink transition-colors pb-1"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 md:gap-6">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "de" ? "en" : "de")}
            className="flex items-center gap-1.5 text-xs font-semibold tracking-[0.15em] uppercase text-ink hover:text-green transition-colors"
            aria-label="Switch language"
          >
            <Globe size={14} strokeWidth={1.75} />
            {lang === "de" ? "EN" : "DE"}
          </button>

          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center bg-green hover:bg-green-hover text-bone text-sm font-medium px-5 py-2.5 transition-colors duration-300"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-ink"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-bone border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => go(id)}
                  className="text-left font-display text-2xl py-2 text-ink"
                >
                  {label}
                </button>
              ))}
              <a
                href={`mailto:${CONTACT.email}`}
                className="mt-3 text-sm text-muted"
              >
                {CONTACT.email}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
